import { ApolloError, useQuery } from '@apollo/client';
import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import Select, { ActionMeta, components } from 'react-select';
import ReactTooltip from 'react-tooltip';
import { Flex, Text } from 'rebass/styled-components'
import styled from 'styled-components';
import { HoverableCard } from '../../components/HoverableCard';
import { NoConversationIcon } from '../../components/icons/NoConversationIcon';
import { Skeleton } from '../../components/loadingSkeleton/Skeleton';
import PleaseWait from '../../components/PleaseWait';
import { Tag } from '../../components/Tag';
import { OpenedConversationsContext } from '../../contexts/openConversationsCounts';
import { GET_CONVERSATIONS, GET_CONVERSATIONS_SEARCH } from '../../graphql/queries';
import { IConversationListEntry, IConversationsListResult, IGetProviderConversationsInput, IGetProviderConversationsOutput, IGetProviderConversationsSearchInput, IGetProviderConversationsSearchOutput } from '../../graphql/types/queries/conversation';
import { Constants } from '../../utils/Constants';
import { getMockedFirstName, getMockedLastName } from '../../utils/mocks';
import { ToolbarMenuButton } from './components/ConversationToolbar';
import moment from 'moment';

const AvatarImg = styled(Flex)`
  background: #FFE3D8;
  border-radius: 50%;
  border: 0px solid #ccc;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  min-width: 38px;
  & > img {
    width: 38px;
  }
`;
const convertToFriendly = (dt: string) => {
  const m = moment(dt).fromNow()
  
  if (m.indexOf('seconde') < 0 && m.indexOf('minute') < 0 && m.indexOf('hours') < 0) {
    if(moment().diff(dt, 'days')<10){
      return m
    }
    return moment(dt).format('MMM DD, \'YY')
  }
  return m
}

interface IMessagesListProps {
  search?: string | null | undefined
  onSelectConversation: (convo: IConversationListEntry) => void
  data: IConversationsListResult | undefined
  loading: boolean,
  error: ApolloError | undefined
  isGlobalSearch?: boolean
  fetchMore?: () => void
  isFetchingMore?: boolean
  setSelectedStatusDrop?: (status: IOptionsStatus) => void
  selectedStatus?: IOptionsStatus
  refetch?: () => void
  isEmbed?: boolean
}
export interface IOptionsStatus {
  value: 'open' | 'closed';
  label: string;
}

const optionsStatus: Array<IOptionsStatus> = [
  { value: 'open', label: `open conversations` },
  { value: 'closed', label: `closed conversations` },
]

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    borderBottom: '0px dotted pink',
    color: '#344F7A',
    padding: '8px 12px',
    backgroundColor: 'transparent'
  }),
  control: (provided: any) => ({
    ...provided,
    border: 0,
    boxShadow: 'none',
    borderRadius: '10px',
    background: 'transparent',
    paddingLeft: '8px',
    minWidth: '200px'
  }),
  singleValue: (provided: any, state: any) => {
    return {
      ...provided,
      color: '#2196F3',
      fontSize: '12px'
    };
  }, dropdownIndicator: (base: any) => ({
    ...base,
    color: "#2196F3" // Custom colour
  })
  ,
  Input: (provided: any) => ({
    ...provided,
    color: '#2196F3',
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    backgroundColor: '#2196F3',
  }),
  indicatorContainer: (provided: any) => ({
    ...provided,
    color: '#2196F3',
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '10px',
    border: '1px solid #EBEBEB',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
  }),
}
const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <Flex alignItems={'center'}>
        <Text>{props.children}</Text>
      </Flex>
    </components.Option>
  );
};

const MessageEntry = ({ entry, hoveredId, setHover, onSelectConversation, selectedConversationId, isEmbed }: { isEmbed?: boolean, selectedConversationId: string | null | undefined, entry: IConversationListEntry, setHover: (id: string | null) => void, hoveredId: string | null, onSelectConversation: (convo: IConversationListEntry) => void }) => {
  const navigate = useNavigate()

  let firstName = entry.primaryCustomer.first
  let lastName = entry.primaryCustomer.last

  if (Constants.IS_SCRAMBLING_MODE) {
    firstName = getMockedFirstName()
    lastName = getMockedLastName()
  }
  if (entry.id.toString() === '2182269688') {
    console.log({ entry })
  }

  return (
    <HoverableCard onClick={() => {
      navigate(`/messages/${entry.id}`)
      onSelectConversation(entry)
    }}
      isEmbed={isEmbed} id={entry.id.toString()} hoveredId={hoveredId} setHover={setHover} style={{
        border: '0px', boxShadow: 'initial', cursor: 'pointer',
        backgroundColor: !!selectedConversationId && selectedConversationId === entry.id.toString() ? '#FFEDEA' : ''
      }}>
      <Flex data-tip data-for={`ttTagsConvo${entry.id}`} paddingX={'8px'} paddingY='4px' alignItems={'center'} style={{ position: 'relative', overflow: 'hidden' }}>
        <AvatarImg style={{ alignSelf: 'center', marginRight: '6px', position: 'relative' }}>
          <img
            alt=""
            src={entry.primaryCustomer.photoUrl}
          />
          {entry.isClinicalTeam ? (
            <svg width="28" height="28" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M27.9317 23.9153C27.1019 23.0849 26.1323 22.4071 25.0674 21.913C26.5691 20.6963 27.5272 18.8398 27.5272 16.7579C27.5272 13.0847 24.464 10.0713 20.7908 10.1276C17.174 10.184 14.26 13.1312 14.26 16.7579C14.26 18.8398 15.2214 20.6963 16.7198 21.913C15.6547 22.4067 14.6851 23.0846 13.8556 23.9153C12.0455 25.7287 11.0178 28.1223 10.9515 30.6749C10.9506 30.7103 10.9568 30.7455 10.9697 30.7784C10.9827 30.8114 11.0021 30.8414 11.0268 30.8668C11.0515 30.8921 11.081 30.9122 11.1136 30.926C11.1463 30.9397 11.1813 30.9468 11.2167 30.9468H13.0732C13.2157 30.9468 13.3351 30.834 13.3384 30.6915C13.4014 28.7687 14.1804 26.9686 15.5496 25.6027C16.2502 24.8984 17.0836 24.34 18.0014 23.9599C18.9193 23.5798 19.9035 23.3855 20.8969 23.3882C22.9158 23.3882 24.8154 24.1739 26.2443 25.6027C27.6101 26.9686 28.3892 28.7687 28.4555 30.6915C28.4588 30.834 28.5781 30.9468 28.7207 30.9468H30.5772C30.6125 30.9468 30.6476 30.9397 30.6802 30.926C30.7128 30.9122 30.7424 30.8921 30.7671 30.8668C30.7918 30.8414 30.8112 30.8114 30.8241 30.7784C30.837 30.7455 30.8433 30.7103 30.8424 30.6749C30.7761 28.1223 29.7484 25.7287 27.9317 23.9153ZM20.8969 21.0013C19.7631 21.0013 18.6957 20.5604 17.8967 19.7581C17.4958 19.3603 17.179 18.8859 16.9654 18.363C16.7519 17.8401 16.6458 17.2796 16.6535 16.7148C16.6635 15.6275 17.0978 14.5766 17.8569 13.7975C18.6526 12.982 19.7167 12.5278 20.8538 12.5145C21.9777 12.5046 23.0683 12.9422 23.8706 13.7279C24.6928 14.5335 25.1436 15.6109 25.1436 16.7579C25.1436 17.8917 24.7027 18.9559 23.9004 19.7581C23.5068 20.1536 23.0387 20.4671 22.5232 20.6805C22.0076 20.8939 21.4549 21.0029 20.8969 21.0013ZM12.5925 17.6331C12.5626 17.3447 12.5461 17.053 12.5461 16.7579C12.5461 16.2308 12.5958 15.717 12.6886 15.2164C12.7118 15.097 12.6488 14.9744 12.5394 14.9247C12.0886 14.7224 11.6742 14.444 11.3161 14.0926C10.8943 13.6835 10.5623 13.191 10.3414 12.6464C10.1205 12.1019 10.0155 11.5173 10.0332 10.9299C10.063 9.86574 10.4907 8.85462 11.2366 8.09214C12.0554 7.2534 13.1561 6.79591 14.3263 6.80917C15.3838 6.81912 16.4049 7.22688 17.1773 7.94958C17.4392 8.19491 17.6647 8.46675 17.8536 8.75848C17.9199 8.86125 18.0492 8.90435 18.1619 8.86456C18.7454 8.66234 19.362 8.51979 19.9952 8.45349C20.1809 8.4336 20.2869 8.23469 20.2041 8.06893C19.1266 5.93729 16.9254 4.46536 14.3793 4.42558C10.7028 4.36923 7.63965 7.38269 7.63965 11.0526C7.63965 13.1345 8.59773 14.991 10.0995 16.2076C9.04527 16.6949 8.07394 17.3679 7.23189 18.21C5.41519 20.0234 4.38749 22.4169 4.32119 24.9729C4.3203 25.0082 4.32651 25.0434 4.33944 25.0764C4.35237 25.1093 4.37177 25.1394 4.39648 25.1647C4.4212 25.19 4.45074 25.2102 4.48335 25.2239C4.51597 25.2376 4.55101 25.2447 4.5864 25.2447H6.4462C6.58875 25.2447 6.7081 25.132 6.71141 24.9894C6.7744 23.0667 7.55346 21.2665 8.92261 19.9007C9.89727 18.926 11.0907 18.2497 12.3936 17.9215C12.5229 17.8884 12.6091 17.7657 12.5925 17.6331Z" fill="#344F7A" />
            </svg>

          ) : (
            <svg width="28" height="28" viewBox="0 0 39 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.9336 18.9603C17.7425 18.9603 19.0768 17.626 19.0768 15.8171C19.0768 14.0081 17.7425 12.6738 15.9336 12.6738C14.1247 12.6738 12.7903 14.0081 12.7903 15.8171C12.7903 17.626 14.1231 18.9603 15.9336 18.9603Z" fill="#344F7A" />
              <path d="M32.0979 6.38672H6.95184C5.21834 6.38672 3.80859 7.70846 3.80859 9.33352V28.5859C3.80859 30.211 5.21834 31.5327 6.95184 31.5327H32.0979C33.8314 31.5327 35.2411 30.211 35.2411 28.5859V9.33352C35.2411 7.70846 33.8314 6.38672 32.0979 6.38672ZM32.0979 28.3895L6.95184 28.3722V9.52997L32.0979 9.54726V28.3895Z" fill="#344F7A" />
              <path d="M22.668 14.2451H28.9545V17.3884H22.668V14.2451ZM24.2397 20.5316H28.9545V23.6749H24.2397V20.5316ZM21.7722 24.5173C21.7722 22.3578 19.1382 20.1387 15.9336 20.1387C12.7291 20.1387 10.095 22.3578 10.095 24.5173V25.2465H21.7722V24.5173Z" fill="#344F7A" />
            </svg>

          )}
          {entry.tags && entry.tags.findIndex(xx => xx.tag === 'deprecated') > -1 && (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 'calc(50% - 12px)', left: 'calc(50% - 12px)' }}>
              <path d="M9 8H15V6C15 5.16667 14.7083 4.45833 14.125 3.875C13.5417 3.29167 12.8333 3 12 3C11.1667 3 10.4583 3.29167 9.875 3.875C9.29167 4.45833 9 5.16667 9 6V8ZM6 22C5.45 22 4.979 21.804 4.587 21.412C4.195 21.02 3.99934 20.5493 4 20V10C4 9.45 4.196 8.979 4.588 8.587C4.98 8.195 5.45067 7.99933 6 8H7V6C7 4.61667 7.48767 3.43733 8.463 2.462C9.43834 1.48667 10.6173 0.999334 12 1C13.3833 1 14.5627 1.48767 15.538 2.463C16.5133 3.43833 17.0007 4.61733 17 6V8H18C18.55 8 19.021 8.196 19.413 8.588C19.805 8.98 20.0007 9.45067 20 10V11.3C19.7 11.2 19.3873 11.125 19.062 11.075C18.7367 11.025 18.3827 11 18 11V10H6V20H11.3C11.4333 20.4 11.5667 20.746 11.7 21.038C11.8333 21.33 12.0167 21.6507 12.25 22H6ZM18 23C16.6167 23 15.4373 22.5123 14.462 21.537C13.4867 20.5617 12.9993 19.3827 13 18C13 16.6167 13.4877 15.4373 14.463 14.462C15.4383 13.4867 16.6173 12.9993 18 13C19.3833 13 20.5627 13.4877 21.538 14.463C22.5133 15.4383 23.0007 16.6173 23 18C23 19.3833 22.5123 20.5627 21.537 21.538C20.5617 22.5133 19.3827 23.0007 18 23ZM19.3 20C19.4 20.1 19.5167 20.15 19.65 20.15C19.7833 20.15 19.9 20.1 20 20C20.1 19.9 20.15 19.7833 20.15 19.65C20.15 19.5167 20.1 19.4 20 19.3L18.5 17.8V15.5C18.5 15.3667 18.45 15.25 18.35 15.15C18.25 15.05 18.1333 15 18 15C17.8667 15 17.75 15.05 17.65 15.15C17.55 15.25 17.5 15.3667 17.5 15.5V18C17.5 18.0333 17.55 18.15 17.65 18.35L19.3 20Z" fill="#F8F8F8" />
            </svg>
          )}
        </AvatarImg>
        <Flex flexDirection={'column'} flex={1}>
          <Flex >
            <Text fontSize={'15px'} flex={1} fontWeight={500}>{firstName} {lastName}</Text>
            <Text fontSize={'13px'} fontWeight={500}>{convertToFriendly(entry.userUpdatedAt)}</Text>
          </Flex>
          <Text fontSize={'13px'} fontWeight={500}>{entry.subject}</Text>
          <Flex alignItems={'center'}>
            {entry.status === 'pending' && (
              <Flex key={`tagPending-${entry.id}`}>
                <Tag tag={{
                  color: '#bababa',
                  id: -1,
                  tag: 'pending'
                }} />
              </Flex>
            )}
            {!!entry.tags && !selectedConversationId && (
              <>
                {entry.tags.map(x => {
                  return (
                    <Flex key={x.tag}>
                      <Tag tag={x} />
                    </Flex>
                  )
                })}
              </>
            )}
            <Text fontSize={'13px'} color='#93a1b0' style={{ lineClamp: 1, textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'break-word', overflow: 'hidden', maxHeight: '18px', height: '18px' }} flex={1}>{entry.preview === 'initiate_1' ? 'Conversation started' : entry.preview}</Text>
          </Flex>
          {entry.isClinicalTeam && (
            <Flex alignItems={'flex-start'} mt={1}>
              <Flex style={{
                background: '#BCFFEB',
                borderRadius: '8px',
                padding: '4px 12px'
              }}>
                <Text fontSize={'12px'}>Clinical Team</Text>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Text>

        </Text>
        {!!selectedConversationId && entry.tags && entry.tags.length > 0 && (
          <>
            <Flex style={{ position: 'absolute', alignItems: 'flex-end', justifyContent: 'centers', top: -15, left: -35, width: 80, background: entry.tags[0].color, opacity: 0.75, transform: 'rotate(-45deg)' }}>
              <svg style={{ transform: 'rotate(45deg)', marginBottom: 6, marginLeft: 6, opacity: 0 }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.5 20.5C5.23478 20.5 4.98043 20.3947 4.79289 20.2071C4.60536 20.0196 4.5 19.7652 4.5 19.5V4.50002C4.5 4.23481 4.60536 3.98045 4.79289 3.79292C4.98043 3.60538 5.23478 3.50002 5.5 3.50002H11.88C12.0664 3.49873 12.2494 3.54954 12.4084 3.64672C12.5674 3.7439 12.6961 3.88359 12.78 4.05002L13.5 5.50002H18.5C18.7652 5.50002 19.0196 5.60538 19.2071 5.79292C19.3946 5.98045 19.5 6.23481 19.5 6.50002V14.5C19.5 14.7652 19.3946 15.0196 19.2071 15.2071C19.0196 15.3947 18.7652 15.5 18.5 15.5H13.11C12.9253 15.4995 12.7444 15.4478 12.5873 15.3507C12.4302 15.2536 12.3031 15.1149 12.22 14.95L11.5 13.5H6.5V19.5C6.5 19.7652 6.39464 20.0196 6.20711 20.2071C6.01957 20.3947 5.76522 20.5 5.5 20.5V20.5ZM6.5 5.50002V11.5H12.5L13.5 13.5H17.5V7.50002H12.5L11.5 5.50002H6.5Z"
                  fill="white"></path></svg>
            </Flex>
          </>
        )}
        {entry.tags && entry.tags.length > 0 && (
          <ReactTooltip id={`ttTagsConvo${entry.id}`} aria-haspopup='true' >
            <Flex flexDirection={'column'}>
              {entry.tags.map(x => {
                return (
                  <Flex key={x.tag}>
                    <Tag tag={x} />
                  </Flex>
                )
              })}
            </Flex>
          </ReactTooltip>
        )}
      </Flex>
    </HoverableCard>
  )
}

const MessageEntryLoading = ({ id }: { id: string }) => {
  const rndWidthName = [55, 99, 147][Math.floor(Math.random() * 3)]
  const rndWidthSubject = [155, 175, 220][Math.floor(Math.random() * 3)]
  const rndWidthPreview = [95, 200, 250][Math.floor(Math.random() * 3)]
  return (
    <HoverableCard
      id={id}
      hoveredId={undefined}
      setHover={() => { }}
      style={{
        border: '0px', boxShadow: 'initial', cursor: 'pointer',
      }}>
      <Flex paddingX={'8px'} paddingY='4px' alignItems={'center'}>
        <AvatarImg style={{ alignSelf: 'center', marginRight: '6px' }}>

        </AvatarImg>
        <Flex flexDirection={'column'} flex={1}>
          <Flex >
            <Text fontSize={'15px'} flex={1} fontWeight={500}><Skeleton width={rndWidthName} /></Text>
            <Text fontSize={'13px'} fontWeight={500}><Skeleton width={63} /></Text>
          </Flex>
          <Text fontSize={'13px'} fontWeight={500}><Skeleton width={rndWidthPreview} /></Text>
          <Flex alignItems={'center'}>
            <Text fontSize={'13px'} color='#93a1b0' style={{ lineClamp: 1, textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'break-word', overflow: 'hidden', maxHeight: '18px', height: '18px' }} flex={1}>
              <Skeleton width={rndWidthSubject} />
            </Text>
          </Flex>
        </Flex>
        <Text>

        </Text>
      </Flex>
    </HoverableCard>
  )
}

const Wrapper = styled(Flex)`
  overflow: auto;
  flex-direction: column;
  flex: 1;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

export const MessagesList: React.FC<IMessagesListProps> = ({ isEmbed, refetch, selectedStatus, search, onSelectConversation, data, loading, error, isGlobalSearch, fetchMore, isFetchingMore, setSelectedStatusDrop }) => {
  let params = useParams();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  if (!!loading && (!data || !data.conversations)) {
    return (
      <Wrapper flexDirection={'column'} style={{ height: '100%', position: 'relative' }}>
        {!isGlobalSearch && !isEmbed && (
          <Flex mt={2} mr={2} mb={3}>
            <Flex flex={1} >
              {!!refetch && (
                <ToolbarMenuButton onClick={refetch}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 11H7.101L7.102 10.991C7.23257 10.3516 7.48813 9.74434 7.854 9.20399C8.39845 8.4018 9.16215 7.77315 10.054 7.39299C10.356 7.26499 10.671 7.16699 10.992 7.10199C11.6579 6.96698 12.3441 6.96698 13.01 7.10199C13.967 7.29808 14.8451 7.7714 15.535 8.46299L16.951 7.05099C16.3128 6.41262 15.5578 5.90303 14.727 5.54999C14.3033 5.37062 13.8628 5.23394 13.412 5.14199C12.4818 4.953 11.5232 4.953 10.593 5.14199C10.1419 5.23432 9.70101 5.37133 9.277 5.55099C8.02753 6.08109 6.95793 6.96108 6.197 8.08499C5.68489 8.84284 5.32676 9.69398 5.143 10.59C5.115 10.725 5.1 10.863 5.08 11H2L6 15L10 11ZM14 13H16.899L16.898 13.008C16.6367 14.2897 15.8812 15.4171 14.795 16.146C14.2548 16.5122 13.6475 16.7677 13.008 16.898C12.3424 17.033 11.6566 17.033 10.991 16.898C10.3516 16.7674 9.74435 16.5119 9.204 16.146C8.93862 15.9665 8.69085 15.7622 8.464 15.536L7.05 16.95C7.68851 17.5882 8.44392 18.0974 9.275 18.45C9.699 18.63 10.142 18.767 10.59 18.858C11.5198 19.0471 12.4782 19.0471 13.408 18.858C15.2005 18.4859 16.7773 17.4294 17.803 15.913C18.3146 15.1557 18.6724 14.3053 18.856 13.41C18.883 13.275 18.899 13.137 18.919 13H22L18 8.99999L14 13Z" fill="black" />
                  </svg>
                </ToolbarMenuButton>
              )}
            </Flex>
            <Select
              isSearchable={false}
              styles={customStyles}
              options={optionsStatus}
              value={selectedStatus}
              onChange={(option: IOptionsStatus | null, actionMeta: ActionMeta<IOptionsStatus>) => {
                if (!option || !setSelectedStatusDrop) return
                setSelectedStatusDrop(option)

              }}
              components={{ Option }} />
          </Flex>
        )}

        <Flex flex={1} flexDirection='column' paddingX={'8px'} paddingTop={isGlobalSearch ? '20px' : ''} style={{ overflowY: 'auto' }}>
          <>
            {[0, 1, 2, 3, 4, 5, 6].map(x => {
              return (
                <MessageEntryLoading key={x} id={x.toString()} />
              )
            })}
          </>
        </Flex>
      </Wrapper>)
  }
  let emptyState

  if (!data || !data || (data.conversations || []).length < 1 && (isGlobalSearch && !selectedStatus)) {
    emptyState = <Flex flexDirection={'column'} justifyContent='center' alignItems={'center'}>
      <Text>No conversation</Text>
    </Flex>
  } else {
    if (!isGlobalSearch && !isEmbed) {
      if (!!search) {
        if (data.conversations!.filter(x => {
          if (!!search) {
            return x.primaryCustomer.first.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
              x.primaryCustomer.last.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
              (x.customFields && (x.customFields.find(y => y.id === 18618)?.text || '').indexOf(search) > -1)
          }
        }).length < 1) {
          emptyState = <Flex flexDirection={'column'} justifyContent='center' alignItems={'center'}>
            <Text>No result for your search</Text>
          </Flex>
        }
      } else {
        if (!selectedStatus) {
          emptyState = <Flex flexDirection={'column'} justifyContent='center' alignItems={'center'}>
            <NoConversationIcon style={{ maxWidth: '50%' }} />
            <Text>No conversation</Text>
          </Flex>
        } else {
          if (data.conversations!.filter(x => {
            if (selectedStatus.value === 'open') {
              return (x.status === 'open' || x.status === 'active' || x.status === 'pending')
            } else {
              return (x.status !== 'open' && x.status !== 'active' && x.status !== 'pending')
            }
          }).length < 1) {
            emptyState = <Flex flexDirection={'column'} justifyContent='center' alignItems={'center'}>
              <NoConversationIcon style={{ maxWidth: '50%' }} />
              <Text>{`No ${selectedStatus.value} conversation`}</Text>
            </Flex>
          }
        }
      }
    }
  }

  const onScroll = (e: any) => {
    if (fetchMore && !!data && data.page && !isFetchingMore) {
      if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight) {
        // if there are no more posts to fetch, don't do anything
        if (data.page.number >= data.page.totalPages) return;

        setTimeout(() => {
          // setIsFetchMoreLoading(true);
          fetchMore();
        }, 300);

        return;
      }
    }
  }

  return (
    <Flex flexDirection={'column'} style={{ height: '100%', position: 'relative' }}>
      {!isGlobalSearch && !isEmbed && (
        <Flex mt={2} mr={2} mb={3}>
          <Flex flex={1} >
            {!!refetch && (
              <ToolbarMenuButton onClick={refetch}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 11H7.101L7.102 10.991C7.23257 10.3516 7.48813 9.74434 7.854 9.20399C8.39845 8.4018 9.16215 7.77315 10.054 7.39299C10.356 7.26499 10.671 7.16699 10.992 7.10199C11.6579 6.96698 12.3441 6.96698 13.01 7.10199C13.967 7.29808 14.8451 7.7714 15.535 8.46299L16.951 7.05099C16.3128 6.41262 15.5578 5.90303 14.727 5.54999C14.3033 5.37062 13.8628 5.23394 13.412 5.14199C12.4818 4.953 11.5232 4.953 10.593 5.14199C10.1419 5.23432 9.70101 5.37133 9.277 5.55099C8.02753 6.08109 6.95793 6.96108 6.197 8.08499C5.68489 8.84284 5.32676 9.69398 5.143 10.59C5.115 10.725 5.1 10.863 5.08 11H2L6 15L10 11ZM14 13H16.899L16.898 13.008C16.6367 14.2897 15.8812 15.4171 14.795 16.146C14.2548 16.5122 13.6475 16.7677 13.008 16.898C12.3424 17.033 11.6566 17.033 10.991 16.898C10.3516 16.7674 9.74435 16.5119 9.204 16.146C8.93862 15.9665 8.69085 15.7622 8.464 15.536L7.05 16.95C7.68851 17.5882 8.44392 18.0974 9.275 18.45C9.699 18.63 10.142 18.767 10.59 18.858C11.5198 19.0471 12.4782 19.0471 13.408 18.858C15.2005 18.4859 16.7773 17.4294 17.803 15.913C18.3146 15.1557 18.6724 14.3053 18.856 13.41C18.883 13.275 18.899 13.137 18.919 13H22L18 8.99999L14 13Z" fill="black" />
                </svg>
              </ToolbarMenuButton>
            )}
          </Flex>
          <Select
            isSearchable={false}
            styles={customStyles}
            options={optionsStatus}
            value={selectedStatus}
            onChange={(option: IOptionsStatus | null, actionMeta: ActionMeta<IOptionsStatus>) => {
              if (!option || !setSelectedStatusDrop) return
              setSelectedStatusDrop(option)

            }}
            components={{ Option }} />
        </Flex>
      )}
      <Wrapper onScroll={onScroll} flex={1} flexDirection='column' paddingX={'8px'} paddingTop={isGlobalSearch ? '20px' : ''} style={{ overflowY: 'auto', justifyContent: !!emptyState ? 'center' : '' }}>
        {!!emptyState ? (
          <>
            {emptyState}
          </>
        ) : (
          <>
            {data?.conversations?.filter(x => {
              if (!!search) {
                return x.primaryCustomer.first.toLowerCase().indexOf(search.toLowerCase()) > -1 || x.primaryCustomer.last.toLowerCase().indexOf(search.toLowerCase()) > -1
              }
              return true
            })
              // .sort((a, b) => {
              //   const da = moment(a.userUpdatedAt)
              //   const db = moment(b.userUpdatedAt)

              //   if (da.isAfter(db)) return 1
              //   return -1
              // })
              .map(x => {
                return (
                  <MessageEntry selectedConversationId={(params || ({} as any))['*']} entry={x} hoveredId={hoveredRow} setHover={setHoveredRow} key={x.id} onSelectConversation={onSelectConversation} isEmbed={isEmbed} />
                )
              })}
          </>)}
      </Wrapper>
      {!!isFetchingMore && (
        <Flex width={'100%'}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0
          }}
          padding={4} backgroundColor='white' opacity={0.85} alignItems='center' justifyContent={'center'}>
          <PleaseWait text='Loading more' />
        </Flex>
      )}
    </Flex>);
}

interface IMessageListNoSearch {
  onSelectConversation: (convo: IConversationListEntry) => void
  isEmbed?: boolean
}
interface IMessageListSearch {
  onSelectConversation: (convo: IConversationListEntry) => void
  search: string
}

const MessagesListOpened = ({ onSelectConversation, setSelectedStatusDrop, selectedStatus, isEmbed }: IMessageListNoSearch & {
  setSelectedStatusDrop: (status: IOptionsStatus) => void
  selectedStatus: IOptionsStatus
}) => {
  const openedConversationsCount = useContext(OpenedConversationsContext)
  const [lastOpenedCount, setLastOpenedCount] = useState<number>(openedConversationsCount);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const { data, loading, error, fetchMore, refetch, called } = useQuery<IGetProviderConversationsOutput, IGetProviderConversationsInput>(GET_CONVERSATIONS, {
    variables: {
      status: 'open'
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (openedConversationsCount !== lastOpenedCount) {
      if (called) {
        refetch()
      }
      setLastOpenedCount(openedConversationsCount)
    }

    return () => {

    }
  }, [openedConversationsCount]);

  const onFetchMore = () => {
    if (!data ||
      !data.getProviderConversations ||
      !data.getProviderConversations.page) return
    setIsFetchingMore(true)
    fetchMore({
      variables: {
        pageNumber: data.getProviderConversations.page.number + 1
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        setIsFetchingMore(false)
        if (!fetchMoreResult || !fetchMoreResult.getProviderConversations || !fetchMoreResult.getProviderConversations.conversations || fetchMoreResult.getProviderConversations.conversations.length < 1) {
          // setHasReachedEnd(true);
          return previousResult;
        }
        return Object.assign({}, previousResult, {
          getProviderConversations: {
            conversations: [
              ...previousResult.getProviderConversations.conversations || [],
              ...fetchMoreResult.getProviderConversations.conversations
            ],
            _links: fetchMoreResult.getProviderConversations._links,
            page: fetchMoreResult.getProviderConversations.page
          }
        });
      },
    })
  }

  const ref = () => {
    refetch()
  }

  return <MessagesList setSelectedStatusDrop={setSelectedStatusDrop}
    refetch={ref}
    isEmbed={isEmbed}
    fetchMore={onFetchMore}
    selectedStatus={selectedStatus}
    isFetchingMore={isFetchingMore}
    onSelectConversation={onSelectConversation}
    data={data?.getProviderConversations}
    loading={loading}
    error={error} />
}

const MessagesListClosed = ({ onSelectConversation, setSelectedStatusDrop, selectedStatus }: IMessageListNoSearch & {
  setSelectedStatusDrop: (status: IOptionsStatus) => void
  selectedStatus: IOptionsStatus
}) => {
  const openedConversationsCount = useContext(OpenedConversationsContext)
  const [lastOpenedCount, setLastOpenedCount] = useState<number>(openedConversationsCount);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const { data, loading, error, fetchMore, refetch, called } = useQuery<IGetProviderConversationsOutput, IGetProviderConversationsInput>(GET_CONVERSATIONS, {
    variables: {
      status: 'closed'
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  console.log({ data })
  useEffect(() => {
    if (openedConversationsCount !== lastOpenedCount) {
      if (called) {
        refetch()
      }
      setLastOpenedCount(openedConversationsCount)
    }

    return () => {

    }
  }, [openedConversationsCount]);

  const onFetchMore = () => {
    if (!data ||
      !data.getProviderConversations ||
      !data.getProviderConversations.page) return
    setIsFetchingMore(true)
    fetchMore({
      variables: {
        pageNumber: data.getProviderConversations.page.number + 1
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        setIsFetchingMore(false)
        if (!fetchMoreResult || !fetchMoreResult.getProviderConversations || !fetchMoreResult.getProviderConversations.conversations || fetchMoreResult.getProviderConversations.conversations.length < 1) {
          // setHasReachedEnd(true);
          return previousResult;
        }
        return Object.assign({}, previousResult, {
          getProviderConversations: {
            conversations: [
              ...previousResult.getProviderConversations.conversations || [],
              ...fetchMoreResult.getProviderConversations.conversations
            ],
            _links: fetchMoreResult.getProviderConversations._links,
            page: fetchMoreResult.getProviderConversations.page
          }
        });
      },
    })
  }
  const ref = () => {
    refetch()
  }
  return <MessagesList
    fetchMore={onFetchMore}
    isFetchingMore={isFetchingMore}
    setSelectedStatusDrop={setSelectedStatusDrop}
    selectedStatus={selectedStatus}
    onSelectConversation={onSelectConversation}
    data={data?.getProviderConversations}
    loading={loading}
    refetch={ref}
    error={error} />
}

export const MessagesListNoSearch = ({ onSelectConversation, isEmbed }: IMessageListNoSearch) => {
  // const [selectedStatus, setselectedStatus] = useState<string>('open');
  const [selectedStatus, setselectedStatus] = useState<IOptionsStatus>(optionsStatus.find(x => x.value === 'open')!);

  if (selectedStatus.value === 'open') {
    return <MessagesListOpened selectedStatus={selectedStatus} setSelectedStatusDrop={setselectedStatus} onSelectConversation={onSelectConversation} isEmbed={isEmbed} />
  } else {
    return <MessagesListClosed selectedStatus={selectedStatus} setSelectedStatusDrop={setselectedStatus} onSelectConversation={onSelectConversation} />
  }
}

export const MessagesListSearch = ({ onSelectConversation, search }: IMessageListSearch) => {
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const navigate = useNavigate()
  const [onlyMine, setOnlyMine] = useState<boolean>(true)
  const { data, loading, error, fetchMore } = useQuery<IGetProviderConversationsSearchOutput, IGetProviderConversationsSearchInput>(GET_CONVERSATIONS_SEARCH,
    {
      variables: {
        search,
        assignedToMe: onlyMine
      },
      onCompleted: (d) => {
        if (!!d && !!d.getProviderSearchConversations && !!d.getProviderSearchConversations.conversations && d.getProviderSearchConversations.conversations.length === 1) {
          // onSelectConversation(d.getProviderSearchConversations.conversations[0])
          navigate(`/messages/${d.getProviderSearchConversations.conversations[0].id}`)
        }
      }
    })

  const onFetchMore = () => {
    if (!data ||
      !data.getProviderSearchConversations ||
      !data.getProviderSearchConversations.page) return
    setIsFetchingMore(true)
    fetchMore({
      variables: {
        search,
        assignedToMe: onlyMine,
        pageNumber: data.getProviderSearchConversations.page.number + 1
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        setIsFetchingMore(false)
        if (!fetchMoreResult || !fetchMoreResult.getProviderSearchConversations || !fetchMoreResult.getProviderSearchConversations.conversations || fetchMoreResult.getProviderSearchConversations.conversations.length < 1) {
          return previousResult;
        }
        return Object.assign({}, previousResult, {
          getProviderSearchConversations: {
            conversations: [
              ...previousResult.getProviderSearchConversations.conversations || [],
              ...fetchMoreResult.getProviderSearchConversations.conversations
            ],
            _links: fetchMoreResult.getProviderSearchConversations._links,
            page: fetchMoreResult.getProviderSearchConversations.page
          }
        });
      },
    })
  }

  return (
    <Flex flexDirection={'column'} style={{ overflow: 'hidden', flex: 1 }}>
      <Flex alignItems={'center'} alignSelf={'flex-end'} mr={4} mt={2}>
        <Flex
          mr={2}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setOnlyMine(!onlyMine)
          }} style={{ cursor: 'pointer' }}>
          {onlyMine ? (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5V15C0 16.3261 0.526784 17.5979 1.46447 18.5355C2.40215 19.4732 3.67392 20 5 20H15C16.3261 20 17.5979 19.4732 18.5355 18.5355C19.4732 17.5979 20 16.3261 20 15V5C20 3.67392 19.4732 2.40215 18.5355 1.46447C17.5979 0.526784 16.3261 0 15 0H5ZM13.73 8.684C13.8198 8.58814 13.8899 8.47552 13.9362 8.35257C13.9825 8.22963 14.0041 8.09878 13.9998 7.96747C13.9956 7.83617 13.9655 7.707 13.9113 7.58732C13.8571 7.46765 13.7799 7.35982 13.684 7.27C13.5881 7.18018 13.4755 7.11011 13.3526 7.06381C13.2296 7.01751 13.0988 6.99588 12.9675 7.00015C12.8362 7.00442 12.707 7.03451 12.5873 7.08871C12.4677 7.1429 12.3598 7.22014 12.27 7.316L9.187 10.606L7.664 9.253C7.46446 9.08712 7.20825 9.0053 6.9495 9.02482C6.69075 9.04434 6.44972 9.16368 6.27732 9.35762C6.10493 9.55156 6.01467 9.80492 6.02562 10.0642C6.03657 10.3234 6.14787 10.5683 6.336 10.747L8.586 12.747C8.78069 12.9199 9.03515 13.0101 9.29529 12.9983C9.55543 12.9866 9.80072 12.8738 9.979 12.684L13.729 8.684H13.73Z" fill="#344F7A" />
          </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2091 4.79086 21 7 21H17C19.2091 21 21 19.2091 21 17V7C21 4.79086 19.2091 3 17 3Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          )}
        </Flex>
        <Text>Assigned to me</Text>
      </Flex>
      <MessagesList
        isGlobalSearch
        onSelectConversation={onSelectConversation}
        data={data?.getProviderSearchConversations}
        loading={loading}
        error={error}
        fetchMore={onFetchMore}
        isFetchingMore={isFetchingMore} />
    </Flex>)
}